import {
  Scene,
  Vector3,
  MeshBuilder,
  StandardMaterial,
  Color3,
  Mesh,
  PointLight,
  ParticleSystem,
  GPUParticleSystem,
} from "@babylonjs/core";
import {
  createWaterMaterial,
  createFountainJet,
  createFountainMist,
  createBasinFloorMaterial,
  markWaterMesh,
} from "../water.js";
import type { WaterHandle } from "../water.js";
import { BaseZone } from "./BaseZone.js";
import { getSharedMaterials } from "../materials.js";
import { DynamicTextureSurface } from "../surfaces/DynamicTextureSurface.js";
import type { SurfaceTheme } from "../surfaces/types.js";
import { MallLayout, WingName, ATRIUM_HALF, CORRIDOR_HALF_WIDTH, FLOOR_HEIGHT } from "@mall-mart/shared";
import { mergeStaticChildren } from "../mergeStatic.js";
import { addContactShadow, buildBench, buildPlanterTree, buildGlassRailing, buildHoloPedestal, HoloPedestalHandle } from "../props.js";
import { PropLibrary } from "../propLibrary.js";
import { MallStatsDisplays } from "../MallStatsDisplays.js";
import type { ConveyorSurface } from "../../player/controllerMath.js";

const WALL_HEIGHT: number = 15;
const FOOD_COURT_OPENING: number = 12;
const GALLERY_WIDTH: number = 4;
const DOOR_TOP: number = 5;            // ground openings are 5 m tall
const UPPER_DOOR_TOP: number = 12;     // upper openings span y 7..12

/** Axis-aligned conveyor volume; the character controller adds `velocity`
 *  to the player while their feet are inside. The optional sloped `surface`
 *  restricts the grab to a band above the ramp itself. */
export interface EscalatorVolume {
  min: Vector3;
  max: Vector3;
  velocity: Vector3;
  surface?: ConveyorSurface;
}

// Wayfinding signage: dark slate panels with light tracked type and the UI's
// blue accent. The old white panels bloomed to unreadable under the daylight
// rig; dark self-lit signs read like real mall wayfinding and glow softly.
const SIGN_THEME: SurfaceTheme = {
  background: "#161b21",
  foreground: "#eef3f7",
  accent: "#6fb3e0",
  fontFamily: "sans-serif",
  fontFamilySerif: "serif",
};

/**
 * Central Mall Mart atrium: a 70x70 two-story hall with a skylight grid,
 * fountain, benches, planters, directory kiosks, and openings on each side —
 * the north/east/west wing corridors (both floors) and the food court to the
 * south. A gallery ring at y=7 connects the three upper wing corridors and is
 * reached by a pair of escalators (up + down) by the fountain. The mall is
 * one continuous walkable space; there are no teleports.
 */
export class AtriumZone extends BaseZone {
  private floor: Mesh | undefined;
  private galleryFloors: Mesh[] = [];
  readonly escalators: EscalatorVolume[] = [];
  readonly holoPedestals: HoloPedestalHandle[] = [];
  statsDisplays: MallStatsDisplays | undefined;
  private readonly layout: MallLayout;
  /** Cosmetic escalator treads cycling along the slope (t in [0,1), dir ±1). */
  private readonly escalatorStepAnims: Array<{ mesh: Mesh; t: number; dir: number }> = [];
  private stepObserver: ReturnType<Scene["onBeforeRenderObservable"]["add"]> | null = null;
  /** Fountain water material (owns its own scroll observer). */
  private water: WaterHandle | undefined;
  /** Jet + mist systems, disposed with the zone. */
  private readonly fountainParticles: Array<ParticleSystem | GPUParticleSystem> = [];

  constructor(scene: Scene, layout: MallLayout) {
    super("atrium", scene, new Vector3(0, 0, 0));
    this.layout = layout;
    this.entryPosition = new Vector3(0, 1.7, -20);
    this.entryRotation = 0;
  }

  async build(): Promise<void> {
    // Scene-wide lighting (IBL, fill, skylight sun + static shadows) lives in
    // world/lighting.ts; the zone only builds geometry and local accents.
    this.buildFloor();
    this.buildWalls();
    this.buildSkylight();
    this.buildGallery();
    this.buildEscalators();
    this.buildPillars();
    this.buildCentralFountain();
    this.buildBenchesAndPlants();
    this.buildDirectoryKiosks();
    this.buildSignage();
    this.buildNeonTrim();
    this.addLights();
    mergeStaticChildren(
      this.root,
      // Escalator steps animate every frame; merging would freeze them.
      (m) => m === this.floor || this.galleryFloors.includes(m) || m.name.includes("-esc-step-")
    );
    // Animated: built after the merge so the spinning cores stay live meshes.
    this.holoPedestals.push(
      buildHoloPedestal(this.scene, this.root, "atrium-holo-w", new Vector3(-8, 0, 20)),
      buildHoloPedestal(this.scene, this.root, "atrium-holo-e", new Vector3(8, 0, 20))
    );
    // Live commerce displays: dynamic textures + a scaling fill mesh, so they
    // also stay out of the static merge.
    this.statsDisplays = new MallStatsDisplays(this.scene, this.root);
  }

  /** Neon accent strips: along the gallery edge fascia and framing each
   *  wing opening, so the atrium reads as a lit destination, not a hallway. */
  private buildNeonTrim(): void {
    const mats = getSharedMaterials(this.scene);
    const half: number = ATRIUM_HALF;
    const strip = (name: string, pos: Vector3, w: number, d: number): void => {
      const s: Mesh = MeshBuilder.CreateBox(name, { width: w, height: 0.09, depth: d }, this.scene);
      s.position = pos;
      s.material = mats.neonAccent;
      s.parent = this.root;
    };
    // White fascia band on each gallery's inner edge with a continuous LED
    // strip along its bottom lip — the balcony line in the reference art.
    const fascia = (name: string, pos: Vector3, w: number, d: number): void => {
      const f: Mesh = MeshBuilder.CreateBox(name, { width: w, height: 1.0, depth: d }, this.scene);
      f.position = pos;
      f.material = mats.corridorWall;
      f.parent = this.root;
    };
    const span: number = half * 2 - GALLERY_WIDTH * 2;
    // Proud of the slab face by 0.12 (not flush): a fascia whose back face
    // is coplanar with the slab's inner face z-fights it, which reads as a
    // shimmering/blinking band along the balcony line as the camera moves.
    fascia("fascia-gallery-n", new Vector3(0, FLOOR_HEIGHT - 0.5, half - GALLERY_WIDTH - 0.12), span, 0.24);
    fascia("fascia-gallery-e", new Vector3(half - GALLERY_WIDTH - 0.12, FLOOR_HEIGHT - 0.5, 0), 0.24, span);
    fascia("fascia-gallery-w", new Vector3(-half + GALLERY_WIDTH + 0.12, FLOOR_HEIGHT - 0.5, 0), 0.24, span);
    strip("neon-gallery-n", new Vector3(0, FLOOR_HEIGHT - 1.05, half - GALLERY_WIDTH - 0.05), span, 0.09);
    strip("neon-gallery-e", new Vector3(half - GALLERY_WIDTH - 0.05, FLOOR_HEIGHT - 1.05, 0), 0.09, span);
    strip("neon-gallery-w", new Vector3(-half + GALLERY_WIDTH + 0.05, FLOOR_HEIGHT - 1.05, 0), 0.09, span);
    // Lintel strips over the ground-floor wing openings + food court.
    const ow: number = CORRIDOR_HALF_WIDTH * 2 + 0.6;
    strip("neon-door-n", new Vector3(0, DOOR_TOP + 0.12, half - 0.28), ow, 0.09);
    strip("neon-door-e", new Vector3(half - 0.28, DOOR_TOP + 0.12, 0), 0.09, ow);
    strip("neon-door-w", new Vector3(-half + 0.28, DOOR_TOP + 0.12, 0), 0.09, ow);
    strip("neon-door-s", new Vector3(0, DOOR_TOP + 0.12, -half + 0.28), FOOD_COURT_OPENING + 0.6, 0.09);
  }

  private buildFloor(): void {
    const mats = getSharedMaterials(this.scene);
    const size: number = ATRIUM_HALF * 2;
    const floor: Mesh = MeshBuilder.CreateGround(
      "atrium-floor", { width: size, height: size }, this.scene
    );
    floor.material = mats.atriumFloor;
    floor.parent = this.root;
    floor.checkCollisions = true;
    this.floor = floor;
  }

  /**
   * Each wall is split around its openings: north/east/west get 10 m wing
   * openings on BOTH floors (ground y 0..5, upper y 7..12) with bands between
   * and above; the south wall gets the single-story food court opening.
   */
  private buildWalls(): void {
    const mats = getSharedMaterials(this.scene);
    const half: number = ATRIUM_HALF;
    const t: number = 0.4;

    const sides: Array<{ name: string; opening: number; axis: "x" | "z"; sign: number; twoFloors: boolean }> = [
      { name: "north", opening: CORRIDOR_HALF_WIDTH * 2, axis: "z", sign: 1, twoFloors: true },
      { name: "south", opening: FOOD_COURT_OPENING, axis: "z", sign: -1, twoFloors: false },
      { name: "east", opening: CORRIDOR_HALF_WIDTH * 2, axis: "x", sign: 1, twoFloors: true },
      { name: "west", opening: CORRIDOR_HALF_WIDTH * 2, axis: "x", sign: -1, twoFloors: true },
    ];
    for (const s of sides) {
      const segW: number = (half * 2 - s.opening) / 2;
      const segOffset: number = s.opening / 2 + segW / 2;
      const pieces: Array<{ suffix: string; lateral: number; w: number; h: number; y: number }> = [
        { suffix: "a", lateral: -segOffset, w: segW, h: WALL_HEIGHT, y: WALL_HEIGHT / 2 },
        { suffix: "b", lateral: segOffset, w: segW, h: WALL_HEIGHT, y: WALL_HEIGHT / 2 },
      ];
      if (s.twoFloors) {
        // Band between the floors, then above the upper opening.
        pieces.push({ suffix: "band", lateral: 0, w: s.opening, h: FLOOR_HEIGHT - DOOR_TOP, y: DOOR_TOP + (FLOOR_HEIGHT - DOOR_TOP) / 2 });
        pieces.push({ suffix: "top", lateral: 0, w: s.opening, h: WALL_HEIGHT - UPPER_DOOR_TOP, y: UPPER_DOOR_TOP + (WALL_HEIGHT - UPPER_DOOR_TOP) / 2 });
      } else {
        pieces.push({ suffix: "lintel", lateral: 0, w: s.opening, h: WALL_HEIGHT - DOOR_TOP, y: DOOR_TOP + (WALL_HEIGHT - DOOR_TOP) / 2 });
      }
      for (const p of pieces) {
        const isZ: boolean = s.axis === "z";
        const wall: Mesh = MeshBuilder.CreateBox(
          `atrium-wall-${s.name}-${p.suffix}`,
          { width: isZ ? p.w : t, height: p.h, depth: isZ ? t : p.w },
          this.scene
        );
        wall.position = isZ
          ? new Vector3(p.lateral, p.y, s.sign * half)
          : new Vector3(s.sign * half, p.y, p.lateral);
        wall.material = mats.corridorWall;
        wall.parent = this.root;
        wall.checkCollisions = true;
      }
    }
  }

  private buildSkylight(): void {
    // Emissive sky panel with a dark mullion grid painted on it.
    const skyMat: StandardMaterial = new StandardMaterial("atrium-sky-mat", this.scene);
    // Bright near-white daylight so the whole hall reads airy and lit from
    // above, like the reference's domed skylight.
    skyMat.diffuseColor = new Color3(0.72, 0.78, 0.86);
    skyMat.emissiveColor = new Color3(0.52, 0.58, 0.66);
    skyMat.specularColor = new Color3(0, 0, 0);
    skyMat.backFaceCulling = false;

    const sky: Mesh = MeshBuilder.CreateGround(
      "atrium-skylight", { width: ATRIUM_HALF * 2, height: ATRIUM_HALF * 2 }, this.scene
    );
    sky.position = new Vector3(0, WALL_HEIGHT, 0);
    sky.rotation.x = Math.PI;
    sky.material = skyMat;
    sky.parent = this.root;

    const mats = getSharedMaterials(this.scene);
    // Mullion grid in both directions reads as a real skylight structure.
    for (let i: number = -3; i <= 3; i++) {
      const beamZ: Mesh = MeshBuilder.CreateBox(
        `atrium-beam-z-${i}`, { width: 0.3, height: 0.45, depth: ATRIUM_HALF * 2 }, this.scene
      );
      beamZ.position = new Vector3(i * 10, WALL_HEIGHT - 0.22, 0);
      beamZ.material = mats.ceiling;
      beamZ.parent = this.root;

      const beamX: Mesh = MeshBuilder.CreateBox(
        `atrium-beam-x-${i}`, { width: ATRIUM_HALF * 2, height: 0.3, depth: 0.2 }, this.scene
      );
      beamX.position = new Vector3(0, WALL_HEIGHT - 0.15, i * 10);
      beamX.material = mats.ceiling;
      beamX.parent = this.root;
    }
  }

  /** Real second story: gallery walkways at y=7 along the north, east, and
   *  west walls connecting the three upper wing corridors, with railings. */
  private buildGallery(): void {
    const mats = getSharedMaterials(this.scene);
    const half: number = ATRIUM_HALF;
    const gw: number = GALLERY_WIDTH;
    const y: number = FLOOR_HEIGHT;

    const slabs: Array<{ name: string; pos: Vector3; w: number; d: number }> = [
      { name: "n", pos: new Vector3(0, y - 0.2, half - gw / 2), w: half * 2, d: gw },
      { name: "e", pos: new Vector3(half - gw / 2, y - 0.2, 0), w: gw, d: half * 2 },
      { name: "w", pos: new Vector3(-half + gw / 2, y - 0.2, 0), w: gw, d: half * 2 },
    ];
    for (const cfg of slabs) {
      const slab: Mesh = MeshBuilder.CreateBox(
        `atrium-gallery-${cfg.name}`, { width: cfg.w, height: 0.4, depth: cfg.d }, this.scene
      );
      slab.position = cfg.pos;
      slab.material = mats.corridorFloor;
      slab.parent = this.root;
      slab.checkCollisions = true;
      this.galleryFloors.push(slab);
    }

    // Glass railings along the inner edges, with gaps where the escalators
    // land on the north gallery (x 2.5..5.5 up, -5.5..-2.5 down). The north
    // segments stop at x = +-(half - gw) so the corners where the east/west
    // galleries meet the north gallery stay walkable.
    const innerN: number = half - gw;       // z = 31 (north gallery inner edge)
    buildGlassRailing(this.scene, this.root, "atrium-rail-n-1",
      new Vector3((-innerN + -5.5) / 2, y, innerN), innerN - 5.5, 0);
    buildGlassRailing(this.scene, this.root, "atrium-rail-n-2",
      new Vector3(0, y, innerN), 5, 0);      // between the escalators
    buildGlassRailing(this.scene, this.root, "atrium-rail-n-3",
      new Vector3((innerN + 5.5) / 2, y, innerN), innerN - 5.5, 0);
    // East/west railings run from the south wall up to the corner opening at
    // z = innerN (their galleries dead-end at the south wall, so guard it all).
    buildGlassRailing(this.scene, this.root, "atrium-rail-e",
      new Vector3(half - gw, y, -gw / 2), half * 2 - gw, Math.PI / 2);
    buildGlassRailing(this.scene, this.root, "atrium-rail-w",
      new Vector3(-half + gw, y, -gw / 2), half * 2 - gw, Math.PI / 2);
  }

  /** Two escalators (up + down) from the atrium floor to the north gallery.
   *  Built as walkable ramps; the conveyor volumes push the player along. */
  private buildEscalators(): void {
    const mats = getSharedMaterials(this.scene);
    const y: number = FLOOR_HEIGHT;
    const zBottom: number = 17;
    const zTop: number = ATRIUM_HALF - GALLERY_WIDTH; // 31, gallery inner edge
    const run: number = zTop - zBottom;               // 14
    const length: number = Math.hypot(run, y);
    const pitch: number = Math.atan2(y, run);

    for (const side of [-1, 1]) {
      const x: number = side * 4;
      const ramp: Mesh = MeshBuilder.CreateBox(
        `atrium-escalator-${side}`, { width: 2.4, height: 0.35, depth: length + 1 }, this.scene
      );
      ramp.position = new Vector3(x, y / 2 - 0.05, (zBottom + zTop) / 2);
      ramp.rotation.x = -pitch;
      ramp.material = mats.metalFixture;
      ramp.parent = this.root;
      ramp.checkCollisions = true;

      // Visible step treads riding on the smooth collision ramp: thin ribbed
      // boxes every ~40cm of run, each level (not pitched), like real steps.
      // Purely cosmetic (the ramp stays the walkable surface); they cycle
      // along the slope at the conveyor speed and wrap at the ends.
      const stepCount: number = Math.floor(run / 0.4);
      for (let i: number = 0; i < stepCount; i++) {
        const t: number = (i + 0.5) / stepCount;
        const step: Mesh = MeshBuilder.CreateBox(
          `atrium-esc-step-${side}-${i}`,
          { width: 2.3, height: 0.16, depth: 0.34 },
          this.scene
        );
        step.position = new Vector3(x, y * t + 0.04, zBottom + run * t);
        step.material = mats.escalatorStep;
        step.parent = this.root;
        this.escalatorStepAnims.push({ mesh: step, t, dir: side });
      }
      // Comb plates where the steps disappear at each end.
      for (const [py, pz] of [[0.12, zBottom - 0.2], [y + 0.12, zTop + 0.2]] as Array<[number, number]>) {
        const comb: Mesh = MeshBuilder.CreateBox(
          `atrium-esc-comb-${side}-${pz.toFixed(0)}`,
          { width: 2.4, height: 0.06, depth: 0.5 },
          this.scene
        );
        comb.position = new Vector3(x, py, pz);
        comb.material = mats.neonAccent;
        comb.parent = this.root;
      }

      // Balustrades: glass panels with a round steel handrail tube on top.
      for (const bSide of [-1, 1]) {
        const bal: Mesh = MeshBuilder.CreateBox(
          `atrium-esc-bal-${side}-${bSide}`, { width: 0.06, height: 1.0, depth: length }, this.scene
        );
        bal.position = new Vector3(x + bSide * 1.25, y / 2 + 0.5, (zBottom + zTop) / 2);
        bal.rotation.x = -pitch;
        bal.material = mats.glass;
        bal.parent = this.root;
        bal.checkCollisions = true;

        const handrail: Mesh = MeshBuilder.CreateCylinder(
          `atrium-esc-rail-${side}-${bSide}`, { diameter: 0.11, height: length, tessellation: 12 }, this.scene
        );
        // Cylinder axis is +y; π/2 about x lays it along +z, minus the ramp
        // pitch so it climbs with the balustrade.
        handrail.rotation.x = Math.PI / 2 - pitch;
        handrail.position = new Vector3(x + bSide * 1.25, y / 2 + 1.05, (zBottom + zTop) / 2);
        handrail.material = mats.metalFixture;
        handrail.parent = this.root;
      }

      // Landing plate at the bottom.
      const plate: Mesh = MeshBuilder.CreateBox(
        `atrium-esc-plate-${side}`, { width: 2.6, height: 0.08, depth: 2 }, this.scene
      );
      plate.position = new Vector3(x, 0.04, zBottom - 1);
      plate.material = mats.metalFixture;
      plate.parent = this.root;
      plate.checkCollisions = true;

      // Conveyor volume: American convention, keep right. Approaching from
      // the atrium (facing north) the RIGHT ramp (x=+4) carries you up (+z)
      // and the left (x=-4) carries you down (-z). The surface slope keeps
      // the push confined to a band above the ramp itself, so players
      // walking underneath it on the atrium floor are not dragged along.
      this.escalators.push({
        min: new Vector3(x - 1.2, -0.5, zBottom - 1.5),
        max: new Vector3(x + 1.2, y + 2, zTop + 0.5),
        velocity: new Vector3(0, 0, side === 1 ? 1.6 : -1.6),
        surface: { z0: zBottom, y0: 0, z1: zTop, y1: y },
      });

      const label: DynamicTextureSurface = new DynamicTextureSurface({
        scene: this.scene,
        name: `atrium-esc-sign-${side}`,
        widthM: 2.2,
        heightM: 0.7,
        resolution: { width: 512, height: 163 },
        theme: SIGN_THEME,
        emissive: true,
        layout: "wayfinding",
      });
      label.placeAt({
        position: new Vector3(x, 2.6, zBottom - 1.6),
        rotationY: Math.PI,
        parent: this.root,
      });
      label.setContent({ title: side === 1 ? "UP ↑" : "DOWN ↓", subtitle: "Upper Floor" });
    }

    // Cycle the treads at the conveyor speed (1.6 m/s along z), wrapping at
    // the comb plates, so the escalator visibly runs.
    const speedT: number = 1.6 / run; // param-space speed per second
    this.stepObserver = this.scene.onBeforeRenderObservable.add(() => {
      const dt: number = this.scene.getEngine().getDeltaTime() / 1000;
      for (const anim of this.escalatorStepAnims) {
        anim.t += anim.dir * speedT * dt;
        anim.t -= Math.floor(anim.t); // wrap into [0,1)
        anim.mesh.position.y = y * anim.t + 0.04;
        anim.mesh.position.z = zBottom + run * anim.t;
      }
    });
  }

  /** The water ring around the planter drum: basin floor, an annular water
   *  surface, a low outer wall, four arcing jets and a mist haze. */
  private buildFountainBasin(): void {
    const mats = getSharedMaterials(this.scene);
    const innerR: number = 2.6;
    const outerR: number = 3.55;
    const waterY: number = 0.42;

    // Basin pan: a shallow dark disc under the water, and the outer wall.
    const pan: Mesh = MeshBuilder.CreateCylinder(
      "fountain-pan", { diameter: outerR * 2, height: 0.2, tessellation: 48 }, this.scene
    );
    pan.position = new Vector3(0, 0.1, 0);
    pan.material = createBasinFloorMaterial(this.scene, "mm-basin-floor");
    pan.parent = this.root;

    const wallProfile: Vector3[] = [
      new Vector3(outerR, 0, 0),
      new Vector3(outerR + 0.2, 0, 0),
      new Vector3(outerR + 0.2, 0.62, 0),
      new Vector3(outerR, 0.62, 0),
    ];
    const wall: Mesh = MeshBuilder.CreateLathe(
      "fountain-wall",
      { shape: wallProfile, tessellation: 48, closed: true, sideOrientation: Mesh.DOUBLESIDE },
      this.scene
    );
    wall.material = mats.planter;
    wall.parent = this.root;

    // Water surface: an annulus (disc with the planter drum punched out).
    const water: Mesh = MeshBuilder.CreateDisc(
      "fountain-water", { radius: outerR, tessellation: 64 }, this.scene
    );
    water.rotation.x = Math.PI / 2;
    water.position = new Vector3(0, waterY, 0);
    this.water = createWaterMaterial(this.scene, "mm-water");
    water.material = this.water.material;
    markWaterMesh(water);
    water.parent = this.root;

    // Four nozzles on the drum shoulder, throwing arcs outward into the ring.
    const nozzleR: number = innerR + 0.15;
    for (let i: number = 0; i < 4; i++) {
      const a: number = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const origin: Vector3 = new Vector3(
        Math.cos(a) * nozzleR, 1.05, Math.sin(a) * nozzleR
      ).addInPlace(this.root.position);
      const jet = createFountainJet(this.scene, `fountain-jet-${i}`, {
        origin,
        // Up and outward: gravity in the particle system drops the arc into
        // the water between the drum and the basin wall.
        direction: new Vector3(Math.cos(a) * 0.55, 1, Math.sin(a) * 0.55),
      });
      if (jet !== null) {
        this.fountainParticles.push(jet);
      }
    }

    const mist = createFountainMist(
      this.scene,
      "fountain-mist",
      new Vector3(0, waterY, 0).addInPlace(this.root.position),
      outerR * 0.9
    );
    if (mist !== null) {
      this.fountainParticles.push(mist);
    }
  }

  override dispose(): void {
    for (const system of this.fountainParticles) {
      system.dispose();
    }
    this.fountainParticles.length = 0;
    this.water?.dispose();
    this.water = undefined;
    if (this.stepObserver !== null) {
      this.scene.onBeforeRenderObservable.remove(this.stepObserver);
      this.stepObserver = null;
    }
    super.dispose();
  }

  private buildPillars(): void {
    const mats = getSharedMaterials(this.scene);
    const positions: Vector3[] = [
      new Vector3(-14, 0, -14),
      new Vector3(14, 0, -14),
      new Vector3(-14, 0, 14),
      new Vector3(14, 0, 14),
    ];
    for (let i: number = 0; i < positions.length; i++) {
      // Tapered column with a plinth and a capital ring, smooth-tessellated.
      const base: Mesh = MeshBuilder.CreateCylinder(
        `pillar-base-${i}`, { diameterTop: 1.7, diameterBottom: 2.3, height: 0.6, tessellation: 32 }, this.scene
      );
      base.position = new Vector3(positions[i].x, 0.3, positions[i].z);
      base.material = mats.metalFixture;
      base.parent = this.root;

      const pillar: Mesh = MeshBuilder.CreateCylinder(
        `pillar-${i}`, { diameterTop: 1.25, diameterBottom: 1.5, height: WALL_HEIGHT - 1.2, tessellation: 32 }, this.scene
      );
      pillar.position = new Vector3(positions[i].x, 0.6 + (WALL_HEIGHT - 1.2) / 2, positions[i].z);
      pillar.material = mats.corridorWall;
      pillar.parent = this.root;
      pillar.checkCollisions = true;

      const capital: Mesh = MeshBuilder.CreateTorus(
        `pillar-cap-${i}`, { diameter: 1.5, thickness: 0.16, tessellation: 32 }, this.scene
      );
      capital.position = new Vector3(positions[i].x, WALL_HEIGHT - 0.7, positions[i].z);
      capital.material = mats.metalFixture;
      capital.parent = this.root;

      addContactShadow(this.scene, this.root, `pillar-${i}`, positions[i], 1.5);
    }
  }

  /** Centerpiece: the white planter drum from the modern-minimalist
   *  reference, now standing in a ring of flowing water. Four nozzles on the
   *  drum arc into the basin, a ring bench wraps the outside, and the LED
   *  ring still glows at the floor line.
   *
   *  Radii (all metres from the origin): planter drum 2.7, water annulus
   *  2.6-3.55, basin wall 3.55-3.75, bench 3.75-4.6, LED ring 4.8. The
   *  whole thing has to stay clear of the directory kiosks at 5.5. */
  private buildCentralFountain(): void {
    const mats = getSharedMaterials(this.scene);

    // White planter drum, now rising out of the water.
    const drum: Mesh = MeshBuilder.CreateCylinder(
      "centerpiece-planter", { diameterTop: 5.4, diameterBottom: 5.0, height: 1.0, tessellation: 48 }, this.scene
    );
    drum.position = new Vector3(0, 0.5, 0);
    drum.material = mats.planter;
    drum.parent = this.root;

    this.buildFountainBasin();

    // Soil fill just below the planter lip.
    const soilMat: StandardMaterial = new StandardMaterial("centerpiece-soil-mat", this.scene);
    soilMat.diffuseColor = new Color3(0.16, 0.13, 0.1);
    soilMat.specularColor = new Color3(0, 0, 0);
    const soil: Mesh = MeshBuilder.CreateDisc(
      "centerpiece-soil", { radius: 2.6, tessellation: 48 }, this.scene
    );
    soil.rotation.x = Math.PI / 2;
    soil.position = new Vector3(0, 0.95, 0);
    soil.material = soilMat;
    soil.parent = this.root;

    // Ring bench, pushed out past the basin wall so sitters face the water.
    const benchProfile: Vector3[] = [
      new Vector3(3.75, 0, 0),
      new Vector3(4.6, 0, 0),
      new Vector3(4.6, 0.45, 0),
      new Vector3(3.75, 0.45, 0),
    ];
    const bench: Mesh = MeshBuilder.CreateLathe(
      "centerpiece-bench", { shape: benchProfile, tessellation: 48, closed: true, sideOrientation: Mesh.DOUBLESIDE }, this.scene
    );
    bench.material = mats.woodFixture;
    bench.parent = this.root;

    // LED underglow ring where the bench meets the floor.
    const led: Mesh = MeshBuilder.CreateTorus(
      "centerpiece-led", { diameter: 9.6, thickness: 0.07, tessellation: 64 }, this.scene
    );
    led.position = new Vector3(0, 0.06, 0);
    led.material = mats.neonAccent;
    led.parent = this.root;

    addContactShadow(this.scene, this.root, "centerpiece", new Vector3(0, 0, 0), 5.4);

    // One invisible collision drum covers planter + basin + bench (lathe
    // walls are too thin to collide reliably). Sized to the bench outer
    // edge; the kiosks at 5.5 leave a 0.9 m walking gap.
    const hull: Mesh = MeshBuilder.CreateCylinder(
      "centerpiece-hull", { diameter: 9.2, height: 1.0, tessellation: 24 }, this.scene
    );
    hull.position = new Vector3(0, 0.5, 0);
    hull.isVisible = false;
    hull.checkCollisions = true;
    hull.parent = this.root;

    // Centerpiece planting: a dense cluster of the scanned potted plants,
    // pots sunk below the drum lip so only the leaf mass emerges (reads as a
    // planted bed; the dry-country shrub scans read as dead twigs here).
    // Stays below y 5.8 where the jumbotron hangs. Fallback when the prop
    // library is unavailable: the old trunk + canopy.
    const lib: PropLibrary = PropLibrary.for(this.scene);
    if (lib.isAvailable("potted_plant_02")) {
      // Hero plant: pot top ends up around the soil line (y 0.95).
      lib.instantiate("potted_plant_02", this.root, new Vector3(0, 0.15, 0), {
        targetHeight: 2.6,
      });
      const ring: number = 5;
      for (let i: number = 0; i < ring; i++) {
        const a: number = (i / ring) * Math.PI * 2 + 0.5;
        lib.instantiate(
          "potted_plant_02",
          this.root,
          new Vector3(Math.cos(a) * 1.55, 0.5, Math.sin(a) * 1.55),
          { targetHeight: 1.35, rotationY: a * 2.7 }
        );
      }
      return;
    }
    const trunk: Mesh = MeshBuilder.CreateCylinder(
      "centerpiece-trunk", { diameterTop: 0.22, diameterBottom: 0.38, height: 2.8, tessellation: 12 }, this.scene
    );
    trunk.position = new Vector3(0, 0.95 + 1.4, 0);
    trunk.material = mats.woodFixture;
    trunk.parent = this.root;

    const blobs: Array<{ p: Vector3; d: number }> = [
      { p: new Vector3(0, 4.3, 0), d: 3.0 },
      { p: new Vector3(1.0, 3.8, 0.5), d: 2.2 },
      { p: new Vector3(-0.9, 3.9, -0.5), d: 2.2 },
      { p: new Vector3(0.2, 4.9, -0.3), d: 1.8 },
      { p: new Vector3(-0.4, 3.6, 0.8), d: 1.8 },
    ];
    for (let i: number = 0; i < blobs.length; i++) {
      const blob: Mesh = MeshBuilder.CreateSphere(
        `centerpiece-canopy-${i}`, { diameter: blobs[i].d, segments: 12 }, this.scene
      );
      blob.position = blobs[i].p;
      blob.material = mats.foliage;
      blob.parent = this.root;
    }
  }

  private buildBenchesAndPlants(): void {
    const spots: Array<{ pos: Vector3; rotY: number }> = [
      { pos: new Vector3(0, 0, -9), rotY: Math.PI },      // backs face the fountain
      { pos: new Vector3(0, 0, 9), rotY: 0 },
      { pos: new Vector3(-9, 0, 0), rotY: -Math.PI / 2 },
      { pos: new Vector3(9, 0, 0), rotY: Math.PI / 2 },
    ];
    for (let i: number = 0; i < spots.length; i++) {
      buildBench(this.scene, this.root, `atrium-bench-${i}`, spots[i].pos, spots[i].rotY);
    }
    const potSpots: Vector3[] = [
      new Vector3(-14, 0, -11), new Vector3(14, 0, -11),
      new Vector3(-14, 0, 11), new Vector3(14, 0, 11),
    ];
    for (let i: number = 0; i < potSpots.length; i++) {
      buildPlanterTree(this.scene, this.root, `atrium-tree-${i}`, potSpots[i], 1.3);
    }
  }

  /** Three kiosks near the fountain, one per wing, listing that wing's stores. */
  private buildDirectoryKiosks(): void {
    const mats = getSharedMaterials(this.scene);
    const wings: WingName[] = ["north", "east", "west"];
    const spots: Array<{ pos: Vector3; rotY: number }> = [
      { pos: new Vector3(0, 0, 5.5), rotY: Math.PI },       // faces the fountain from the north
      { pos: new Vector3(5.5, 0, 0), rotY: -Math.PI / 2 },
      { pos: new Vector3(-5.5, 0, 0), rotY: Math.PI / 2 },
    ];
    for (let i: number = 0; i < wings.length; i++) {
      const wing: WingName = wings[i];
      const stand: Mesh = MeshBuilder.CreateBox(
        `kiosk-stand-${wing}`, { width: 2.2, height: 1.0, depth: 0.5 }, this.scene
      );
      stand.position = new Vector3(spots[i].pos.x, 0.5, spots[i].pos.z);
      stand.rotation.y = spots[i].rotY;
      stand.material = mats.frame;
      stand.parent = this.root;
      stand.checkCollisions = true;
      // Circular: kiosks rotate per wing, an ellipse would need the same yaw.
      addContactShadow(this.scene, this.root, `kiosk-${wing}`, spots[i].pos, 1.2);

      const lines: string[] = [];
      for (const floorIdx of [0, 1]) {
        const names: string[] = this.layout.wings[wing]
          .filter((p) => p.shop !== null && p.floor === floorIdx)
          .map((p) => (p.shop as { name: string }).name);
        if (names.length === 0) {
          continue;
        }
        lines.push(floorIdx === 0 ? "— Ground Floor" : "— Upper Floor");
        lines.push(...names);
      }
      const board: DynamicTextureSurface = new DynamicTextureSurface({
        scene: this.scene,
        name: `kiosk-board-${wing}`,
        widthM: 2.6,
        heightM: 3.4,
        resolution: { width: 768, height: 1004 },
        theme: SIGN_THEME,
        emissive: true,
        layout: "list",
        singleSided: true,
      });
      board.placeAt({
        position: new Vector3(spots[i].pos.x, 2.6, spots[i].pos.z),
        rotationY: spots[i].rotY,
        parent: this.root,
      });
      board.setContent({
        title: this.layout.wingTitles[wing],
        bodyLines: lines,
      });
    }
  }

  private buildSignage(): void {
    // Wing names sit high on the wall band ABOVE the upper openings (y > 12)
    // so nothing hangs in the gallery walkway; MALL MART is a double-sided
    // ceiling banner suspended over the fountain.
    const signs: Array<{ title: string; subtitle?: string; pos: Vector3; rotY: number }> = [
      // rotY is the facing direction (yaw of the sign's normal toward the
      // viewer): east wall signs face -x (rotY -PI/2), west wall face +x.
      { title: this.layout.wingTitles.north, pos: new Vector3(0, 13.2, ATRIUM_HALF - 0.25), rotY: Math.PI },
      { title: this.layout.wingTitles.east, pos: new Vector3(ATRIUM_HALF - 0.25, 13.2, 0), rotY: -Math.PI / 2 },
      { title: this.layout.wingTitles.west, pos: new Vector3(-ATRIUM_HALF + 0.25, 13.2, 0), rotY: Math.PI / 2 },
      { title: "FOOD COURT", subtitle: "coming soon", pos: new Vector3(0, 7, -ATRIUM_HALF + 0.25), rotY: 0 },
      // Two single-sided faces back to back so the banner reads from both
      // sides (a double-sided plane mirrors the text on its back face).
      { title: "MALL MART", subtitle: "everything under one roof", pos: new Vector3(0, 12.8, 0.05), rotY: Math.PI },
      { title: "MALL MART", subtitle: "everything under one roof", pos: new Vector3(0, 12.8, -0.05), rotY: 0 },
    ];
    // Upstairs wayfinding: you step off the escalator facing the north wall,
    // so hang a directional sign there pointing to the two side galleries,
    // and one at each end of the north gallery marking the turn.
    const gallerySignY: number = FLOOR_HEIGHT + 3.2; // clears heads on the gallery
    signs.push({
      title: "UPPER FLOOR",
      subtitle: `← ${this.layout.wingTitles.west}   |   ${this.layout.wingTitles.east} →`,
      pos: new Vector3(0, gallerySignY, ATRIUM_HALF - 0.25),
      rotY: Math.PI,
    });
    signs.push({
      title: `${this.layout.wingTitles.east} →`,
      subtitle: "continue along the gallery",
      pos: new Vector3(ATRIUM_HALF - 0.25, gallerySignY, 22),
      rotY: -Math.PI / 2,
    });
    signs.push({
      title: `← ${this.layout.wingTitles.west}`,
      subtitle: "continue along the gallery",
      pos: new Vector3(-ATRIUM_HALF + 0.25, gallerySignY, 22),
      rotY: Math.PI / 2,
    });
    for (let i: number = 0; i < signs.length; i++) {
      const surface: DynamicTextureSurface = new DynamicTextureSurface({
        scene: this.scene,
        name: `atrium-sign-${i}`,
        widthM: signs[i].title === "MALL MART" ? 14 : 8,
        heightM: signs[i].title === "MALL MART" ? 2.6 : 1.6,
        resolution: { width: 1024, height: signs[i].title === "MALL MART" ? 190 : 205 },
        theme: SIGN_THEME,
        emissive: true,
        singleSided: true,
        layout: "wayfinding",
      });
      surface.placeAt({ position: signs[i].pos, rotationY: signs[i].rotY, parent: this.root });
      surface.setContent({ title: signs[i].title, subtitle: signs[i].subtitle });
    }
  }

  private addLights(): void {
    const spots: Vector3[] = [
      new Vector3(0, 9, 0),
      new Vector3(-16, 8, -16),
      new Vector3(16, 8, -16),
      new Vector3(-16, 8, 16),
      new Vector3(16, 8, 16),
    ];
    for (let i: number = 0; i < spots.length; i++) {
      const light: PointLight = new PointLight(`atrium-accent-${i}`, spots[i], this.scene);
      light.diffuse = new Color3(0.95, 0.97, 1.0);
      // Trimmed from 0.8/0.5: with the skylight sun + IBL these are accents,
      // not the main illumination (over-stacking lights re-creates the wash).
      light.intensity = i === 0 ? 0.55 : 0.35;
      light.range = 30;
      light.parent = this.root;
    }
  }

  override floorMeshes(): Mesh[] {
    const list: Mesh[] = [...this.galleryFloors];
    if (this.floor !== undefined) {
      list.push(this.floor);
    }
    return list;
  }
}
